import {
    Avatar,
    CircularProgress,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem
} from '@material-ui/core';
import {
    Check,
    Delete,
    MoreVert as MoreVertIcon,
    NotInterested,
    Person as SocialPerson,
    Work as ActionWork
} from '@material-ui/icons';
import * as React from 'react';
import { connect } from 'react-redux';

import { Permissions } from 'shared/models/permission';
import { hasRole } from 'shared/models/user';

import { deletePersonContact, getConfirmation, updatePersonContact } from '../actions';
import { Contact, ContactType, RequestErrors, State } from '../state';

const avatarSize = 18;
const iconSizeRatio = 0.75;
const colorPurple300 = '#ba68c8';
const colorLightBlue200 = '#81d4fa';
const colorBrown500 = '#795548';
const colorGreen300 = '#81c784';
const colorRed400 = '#ef5350';
const colorRed600 = '#e53935';

interface OwnProps {
    contact: Contact;
    personId: string;
}

interface ConnectedProps {
    pendingRequests: Map<string, RequestErrors>;
    userPermissions: Permissions;
}

interface ConnectedDispatch {
    updatePersonContact: (
        personId: string,
        contact: Contact,
        attribute: keyof Contact,
        value: boolean | ContactType
    ) => void;
    deletePersonContact: (personId: string, channel: string, value: string) => void;
    getConfirmation: (onConfirm: () => void, description?: string | JSX.Element, title?: string) => void;
}

type ContactAttributesProps = OwnProps & ConnectedProps & ConnectedDispatch;

interface ContactAttributesState {
    menuAnchor: HTMLElement | null;
}

class ContactAttributesComponent extends React.Component<ContactAttributesProps, ContactAttributesState> {
    state: ContactAttributesState = {
        menuAnchor: null
    };

    handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        if (!this.isDisabled()) {
            this.setState({ menuAnchor: event.currentTarget });
        }
    };

    handleMenuClose = () => {
        this.setState({ menuAnchor: null });
    };

    isDisabled = () => {
        const { contact, pendingRequests, personId } = this.props;
        const updateRequestKey = `contact-update-request-${personId}-${contact.channel}`;
        const deleteRequestKey = `contact-delete-request-${personId}-${contact.channel}-${contact.value}`;
        return (
            (pendingRequests.has(updateRequestKey) && pendingRequests.get(updateRequestKey).isEmpty()) ||
            (pendingRequests.has(deleteRequestKey) && pendingRequests.get(deleteRequestKey).isEmpty())
        );
    };

    handleAttributeSelect = (attribute: keyof Contact) => () => {
        const { contact, personId } = this.props;
        if (!this.isDisabled()) {
            this.props.updatePersonContact(personId, contact, attribute, !contact[attribute]);
            this.handleMenuClose();
        }
    };

    handleContactDelete = () => () => {
        const { personId, contact } = this.props;
        if (!this.isDisabled()) {
            this.props.getConfirmation(
                () => {
                    this.props.deletePersonContact(personId, contact.channel, contact.value);
                },
                `This ${contact.channel} will be removed immediately.`,
                'Confirm'
            );
            this.handleMenuClose();
        }
    };

    handleContactTypeSelect = (contactType: ContactType) => () => {
        const { contact, personId } = this.props;
        if (!this.isDisabled()) {
            this.props.updatePersonContact(
                personId,
                contact,
                'contactType',
                contact.contactType !== contactType ? contactType : ContactType.Unknown
            );
            this.handleMenuClose();
        }
    };

    render() {
        const { contact, userPermissions } = this.props;
        const disabled = this.isDisabled();

        let content: JSX.Element;
        if (disabled) {
            const spinnerSize = 20;
            const spinnerThickness = 2;
            content = <CircularProgress size={spinnerSize} thickness={spinnerThickness} />;
        } else {
            const contactIconBackgroundColor = contact.primary
                ? colorPurple300
                : contact.contactType === ContactType.Personal
                  ? colorLightBlue200
                  : colorBrown500;

            const iconStyle = { color: '#fff', fontSize: avatarSize * iconSizeRatio };
            const personalIcon = <SocialPerson style={iconStyle} />;
            const workIconElement = <ActionWork style={iconStyle} />;

            const contactIcon = (
                <Avatar
                    style={{ width: avatarSize, height: avatarSize, backgroundColor: contactIconBackgroundColor }}
                    className="avatar-contact-attribute"
                >
                    {contact.contactType === ContactType.Personal ? personalIcon : workIconElement}
                </Avatar>
            );
            const verifiedIcon = (
                <Avatar
                    style={{ width: avatarSize, height: avatarSize, backgroundColor: colorGreen300 }}
                    className="avatar-contact-attribute"
                >
                    <Check style={iconStyle} />
                </Avatar>
            );
            const invalidIcon = (
                <Avatar
                    style={{ width: avatarSize, height: avatarSize, backgroundColor: colorRed400 }}
                    className="avatar-contact-attribute"
                >
                    <NotInterested style={iconStyle} />
                </Avatar>
            );

            const removeIcon = (
                <Avatar
                    style={{ width: avatarSize, height: avatarSize, backgroundColor: colorRed600 }}
                    className="avatar-contact-attribute"
                >
                    <Delete style={iconStyle} />
                </Avatar>
            );

            const personIcon = (
                <Avatar
                    style={{ width: avatarSize, height: avatarSize, backgroundColor: colorLightBlue200 }}
                    className="avatar-contact-attribute"
                >
                    <SocialPerson style={iconStyle} />
                </Avatar>
            );

            const workIcon = (
                <Avatar
                    style={{ width: avatarSize, height: avatarSize, backgroundColor: colorBrown500 }}
                    className="avatar-contact-attribute"
                >
                    <ActionWork style={iconStyle} />
                </Avatar>
            );

            const button = (
                <IconButton
                    disableRipple={true}
                    style={{ width: 16, height: 16, padding: 0 }}
                    onClick={this.handleMenuOpen}
                >
                    <MoreVertIcon className="icon-button-more" style={{ height: 16, width: 16 }} />
                </IconButton>
            );

            const renderMenuItem = (
                key: string,
                label: string,
                icon: React.ReactNode,
                onClick: () => void,
                checked?: boolean,
                disabledItem?: boolean
            ) => {
                const trailingIcon = checked ? (
                    <ListItemIcon style={{ minWidth: 0, marginLeft: '10px' }}>
                        <Check />
                    </ListItemIcon>
                ) : null;
                return (
                    <MenuItem key={key} onClick={onClick} disabled={disabledItem} dense={true}>
                        <ListItemIcon style={{ minWidth: 32 }}>{icon}</ListItemIcon>
                        <ListItemText primary={label} />
                        {trailingIcon}
                    </MenuItem>
                );
            };

            const menuItems: JSX.Element[] = [];

            if (contact.contactType !== ContactType.Unknown) {
                const primaryContactTypeIcon = (
                    <Avatar
                        style={{ width: avatarSize, height: avatarSize, backgroundColor: colorPurple300 }}
                        className="avatar-contact-attribute"
                    >
                        {contact.contactType === ContactType.Personal ? personalIcon : workIconElement}
                    </Avatar>
                );
                const primaryMenuItem = renderMenuItem(
                    'primary',
                    'Primary',
                    primaryContactTypeIcon,
                    this.handleAttributeSelect('primary'),
                    contact.primary
                );
                menuItems.push(primaryMenuItem);
            }

            const personalMenuItem = renderMenuItem(
                'personal',
                'Personal',
                personIcon,
                this.handleContactTypeSelect(ContactType.Personal),
                contact.contactType === ContactType.Personal
            );
            menuItems.push(personalMenuItem);

            const workMenuItem = renderMenuItem(
                'work',
                'Work',
                workIcon,
                this.handleContactTypeSelect(ContactType.Work),
                contact.contactType === ContactType.Work
            );
            menuItems.push(workMenuItem);

            const verifiedMenuItem = renderMenuItem(
                'verified',
                'Verified',
                verifiedIcon,
                this.handleAttributeSelect('verified'),
                contact.verified
            );
            menuItems.push(verifiedMenuItem);

            const invalidMenuItem = renderMenuItem(
                'invalid',
                'Invalid',
                invalidIcon,
                this.handleAttributeSelect('invalid'),
                contact.invalid
            );
            menuItems.push(invalidMenuItem);

            if (hasRole(userPermissions, 'contact_editor')) {
                menuItems.push(<Divider key="divider" component="li" />);
                const removeMenuItem = renderMenuItem(
                    'remove',
                    'Remove',
                    removeIcon,
                    this.handleContactDelete(),
                    false,
                    false
                );
                menuItems.push(removeMenuItem);
            }

            content = (
                <div className="contact-attributes">
                    <div className="list-item-menu-button">
                        {button}
                        <Menu
                            anchorEl={this.state.menuAnchor}
                            keepMounted={true}
                            open={Boolean(this.state.menuAnchor)}
                            onClose={this.handleMenuClose}
                            anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        >
                            {menuItems}
                        </Menu>
                    </div>
                    {contact.contactType !== ContactType.Unknown ? contactIcon : null}
                    {contact.verified ? verifiedIcon : null}
                    {contact.invalid ? invalidIcon : null}
                </div>
            );
        }

        return content;
    }
}

const mapStateToProps = (state: State): any => ({
    pendingRequests: state.pendingRequests,
    userPermissions: state.session.userPermissions
});

const mapStateToDispatch = {
    deletePersonContact,
    getConfirmation,
    updatePersonContact
};

export const ContactAttributes = connect<ConnectedProps, ConnectedDispatch, OwnProps>(
    mapStateToProps,
    mapStateToDispatch
)(ContactAttributesComponent);
